import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Sparkles, Wrench, Bug, Compass, X } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";

/**
 * DESIGN — What's New modal (bug-fix iteration).
 *
 * Rules:
 *   - Server state is authoritative. The modal fires when
 *     `user.release_notes.show_modal === true`, which the backend computes
 *     as `latest.show_modal && (latest.version > last_dismissed)`.
 *   - The ONLY action that dismisses the release is the explicit "Got it"
 *     button, which POSTs /api/release-notes/dismiss/{version}.
 *   - "See all releases" does NOT dismiss. It just closes the current modal
 *     visually and navigates to /whats-new — the modal will re-appear on
 *     the next page load until the user explicitly dismisses.
 *   - The × close button and clicking the backdrop close visually but do
 *     NOT dismiss.
 *   - localStorage is an advisory cross-tab signal only. It never suppresses
 *     the modal — an authenticated user with a stale local flag but no
 *     server-side dismissal still sees the modal.
 */

// Cross-tab dismissal signal. Not authoritative — only tells sibling tabs
// to close the visible modal. Server state is the truth.
const DISMISS_BROADCAST_KEY = "amds_release_notes_dismissed_broadcast";

function broadcastDismiss(version) {
  try {
    // Write timestamp+version to force a 'storage' event even on rapid repeat.
    window.localStorage.setItem(DISMISS_BROADCAST_KEY,
      JSON.stringify({ v: version, at: Date.now() }));
  } catch { /* private mode */ }
}

export default function WhatsNewModal() {
  const { user, refresh } = useAuth();
  const [release, setRelease] = useState(null);
  const [open, setOpen] = useState(false);

  // Fetch the latest release payload once we know we need to render.
  useEffect(() => {
    if (!user || typeof user !== "object" || !user.email) {
      setOpen(false);
      return;
    }
    // Defer to MigrationConfirmationModal — it takes precedence during
    // the first-login setup. Prevents modal stacking on new mobile
    // signups (Sep 2026 readiness pass).
    const mig = user.profile_migration;
    if (mig && mig.needs_prompt) {
      setOpen(false);
      return;
    }
    const meta = user.release_notes;
    // AUTHORITATIVE server gate: only render if the server says the modal
    // should fire for this user. No localStorage pre-check — the whole point
    // of this refactor is that stale local flags don't suppress the modal.
    if (!meta || !meta.version || !meta.show_modal) {
      setOpen(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/release-notes/latest");
        if (cancelled) return;
        if (!data || !data.latest) return;
        setRelease(data.latest);
        setOpen(true);
      } catch { /* ignore — never block the app */ }
    })();
    return () => { cancelled = true; };
  }, [user]);

  // Cross-tab close: another tab dismissed → close ours (visually only).
  // Does NOT clear server state (server was already updated by the dismissing tab).
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key !== DISMISS_BROADCAST_KEY || !e.newValue || !release) return;
      try {
        const parsed = JSON.parse(e.newValue);
        if (parsed && parsed.v === release.version) {
          setOpen(false);
        }
      } catch { /* ignore */ }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [release]);

  // The ONLY dismissal path. Everything else is a UI-only close.
  const dismiss = useCallback(async () => {
    if (!release) { setOpen(false); return; }
    setOpen(false);
    broadcastDismiss(release.version);
    try {
      await api.post(`/release-notes/dismiss/${encodeURIComponent(release.version)}`);
    } catch { /* best-effort; modal will re-appear next load if this failed */ }
    try { await refresh(); } catch { /* ignore */ }
  }, [release, refresh]);

  // UI-only close (× button, backdrop click, "See all releases" click).
  // Modal will re-appear on next page load — server state unchanged.
  const closeWithoutDismiss = useCallback(() => {
    setOpen(false);
  }, []);

  if (!open || !release) return null;

  const s = release.sections || {};
  const dateLabel = release.release_date
    ? new Date(release.release_date).toLocaleDateString(undefined, { day: "numeric", month: "long", year: "numeric" })
    : null;

  return (
    <div
      className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center bg-ink-900/40 p-2 animate-fade-up"
      onClick={closeWithoutDismiss}
      data-testid="whats-new-modal"
    >
      <div
        className="bg-white w-full max-w-lg rounded-2xl sm:rounded-3xl shadow-card max-h-[85vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6 pb-4 border-b border-ivory-200">
          <div className="flex items-start justify-between gap-3">
            <div>
              <div className="label-overline inline-flex items-center gap-1.5"><Sparkles size={11} /> What's new · v{release.version}</div>
              <h2 className="font-heading text-2xl font-bold tracking-tight mt-1" data-testid="whats-new-title">{release.title}</h2>
              {dateLabel && <div className="text-xs text-ink-500 mt-1">{dateLabel}</div>}
            </div>
            <button
              type="button"
              onClick={closeWithoutDismiss}
              className="btn-ghost !p-1.5"
              aria-label="Close (will reappear next visit)"
              title="Close for now — modal will reappear until you click Got it"
              data-testid="whats-new-modal-close"
            >
              <X size={18} />
            </button>
          </div>
        </div>

        <div className="p-6 pt-4 space-y-5 text-sm">
          <ReleaseSection icon={<Sparkles size={14} />} label="New" items={s.new_features} tone="sage" testid="whats-new-new" />
          <ReleaseSection icon={<Wrench size={14} />} label="Improvements" items={s.improvements} tone="ink" testid="whats-new-improvements" />
          <ReleaseSection icon={<Bug size={14} />} label="Bug fixes" items={s.bug_fixes} tone="muted" testid="whats-new-fixes" />
          <ReleaseSection icon={<Compass size={14} />} label="Coming soon" items={s.coming_soon} tone="amber" testid="whats-new-soon" />
        </div>

        <div className="p-6 pt-2 flex flex-wrap items-center justify-between gap-3 border-t border-ivory-200">
          <Link
            to="/whats-new"
            onClick={closeWithoutDismiss}
            className="text-sm text-sage-700 hover:text-sage-800 underline underline-offset-2"
            data-testid="whats-new-see-all"
          >
            See all releases
          </Link>
          <button
            type="button"
            onClick={dismiss}
            className="btn-primary text-sm"
            data-testid="whats-new-dismiss"
          >
            Got it
          </button>
        </div>
      </div>
    </div>
  );
}

function ReleaseSection({ icon, label, items, tone, testid }) {
  if (!items || items.length === 0) return null;
  const toneClass = {
    sage: "text-sage-700",
    ink: "text-ink-800",
    muted: "text-ink-500",
    amber: "text-amber-deep",
  }[tone] || "text-ink-700";
  const bulletClass = {
    sage: "bg-sage-500",
    ink: "bg-ink-500",
    muted: "bg-ink-300",
    amber: "bg-amber-deep",
  }[tone] || "bg-ink-500";
  return (
    <div data-testid={testid}>
      <div className={`inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider ${toneClass}`}>{icon} {label}</div>
      <ul className="mt-2 space-y-1.5">
        {items.map((line, i) => (
          <li key={i} className="flex items-start gap-2 text-ink-700 leading-relaxed">
            <span className={`w-1.5 h-1.5 rounded-full mt-2 shrink-0 ${bulletClass}`} />
            <span>{line}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
